import React, { PropsWithChildren } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@components-clean/core/button';
import { DatePickerController } from '@components-clean/core/date-picker';

interface FormSectionProps extends PropsWithChildren {
    title: string;
}

const DatePickers = () => {
    const { control, handleSubmit: handleSubmitHookForm } = useForm<DatePickerPageForm>({
        mode: 'all',
    });

    interface DatePickerPageForm {
        flatOptional: Date;
        flatRequired: Date;
        flatReadonlyOptional: Date;
        flatPlaceholderOptional: Date;
        flatMinOptional: Date;
        flatFutureOptional: Date;
        flatDisabled: Date;
        solidOptional: Date;
        solidRequired: Date;
        solidReadonlyOptional: Date;
        solidPlaceholderOptional: Date;
        solidMaxOptional: Date;
        solidPastOptional: Date;
        solidDisabled: Date;
    }

    const FormSection = ({ title, children }: FormSectionProps) => (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                minWidth: 150,
                maxWidth: 350,
            }}
        >
            <h2 style={{ margin: 0 }}>{title}</h2>
            {children}
        </div>
    );

    return (
        <div className="k-pb-8">
            <form
                // eslint-disable-next-line no-alert -- Allow alert on Dummy Pages.
                onSubmit={handleSubmitHookForm((values) => alert(JSON.stringify(values, null, 2)))}
                className="k-d-flex-col k-gap-8"
                style={{ width: 'fit-content' }}
            >
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <FormSection title="Solid">
                        <DatePickerController
                            control={control}
                            name="solidOptional"
                            label="Date Picker - Optional"
                            data-testid="date-picker-solid-optional"
                            fillMode="solid"
                        />

                        <DatePickerController
                            control={control}
                            name="solidRequired"
                            label="Date Picker - Required"
                            data-testid="date-picker-solid-required"
                            fillMode="solid"
                            required
                        />

                        <DatePickerController
                            control={control}
                            name="solidPlaceholderOptional"
                            label="Date Picker - Placeholder, Optional"
                            data-testid="date-picker-solid-placeholder-optional"
                            fillMode="solid"
                            placeholder="Placeholder"
                        />

                        <DatePickerController
                            control={control}
                            name="solidReadonlyOptional"
                            defaultValue={new Date()}
                            label="Date Picker - Readonly, Optional"
                            data-testid="date-picker-solid-readonly-optional"
                            readonly
                            fillMode="solid"
                        />

                        <DatePickerController
                            control={control}
                            name="solidMaxOptional"
                            label="Date Picker - Max, Optional"
                            data-testid="date-picker-solid-max-optional"
                            fillMode="solid"
                            max={new Date(2024, 5, 30)}
                        />

                        <DatePickerController
                            control={control}
                            name="solidPastOptional"
                            label="Date Picker - Past, Optional"
                            data-testid="date-picker-solid-past-optional"
                            fillMode="solid"
                            max="past"
                        />

                        <DatePickerController
                            control={control}
                            name="solidDisabled"
                            label="Date Picker - Solid, Disabled"
                            data-testid="date-picker-solid-disabled"
                            fillMode="solid"
                            disabled
                            max="past"
                        />
                    </FormSection>

                    <FormSection title="Flat">
                        <DatePickerController
                            control={control}
                            name="flatOptional"
                            label="Date Picker - Optional"
                            data-testid="date-picker-flat-optional"
                            fillMode="flat"
                        />

                        <DatePickerController
                            control={control}
                            name="flatRequired"
                            label="Date Picker - Required"
                            data-testid="date-picker-flat-required"
                            fillMode="flat"
                            required
                        />

                        <DatePickerController
                            control={control}
                            name="flatPlaceholderOptional"
                            label="Date Picker - Placeholder, Optional"
                            data-testid="date-picker-flat-placeholder-optional"
                            fillMode="flat"
                            placeholder="Placeholder"
                        />

                        <DatePickerController
                            control={control}
                            name="flatReadonlyOptional"
                            defaultValue={new Date()}
                            label="Date Picker - Readonly, Optional"
                            data-testid="date-picker-flat-readonly-optional"
                            readonly
                            fillMode="flat"
                        />

                        <DatePickerController
                            name="flatMinOptional"
                            label="Date Picker - Min, Optional"
                            data-testid="date-picker-flat-min-optional"
                            fillMode="flat"
                            min={new Date(2024, 5, 1)}
                            control={control}
                        />

                        <DatePickerController
                            control={control}
                            name="flatFutureOptional"
                            label="Date Picker - Future, Optional"
                            data-testid="date-picker-flat-future-optional"
                            fillMode="flat"
                            min="future"
                        />

                        <DatePickerController
                            control={control}
                            name="flatDisabled"
                            label="Date Picker - Solid, Disabled"
                            data-testid="date-picker-flat-disabled"
                            fillMode="flat"
                            disabled
                            max="past"
                        />
                    </FormSection>
                </div>
                <Button data-testid="submit" primary>
                    Submit
                </Button>
            </form>
        </div>
    );
};

export default DatePickers;
